import { Container, Particle, Texture } from "pixi.js";
import { PianoLayout } from "@/lib/piano/piano-layout";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";
import { ParticleLayer } from "./particle-layer";
import type { LaneStyle, NoteEntry } from "./notes";

/** Une paillette par tant de px² de note consommés par la ligne. */
const EMIT_AREA_PX2 = 18;
const MAX_EMIT_PER_FRAME = 180;
const MAX_PARTICLES = 1600;
const LIFETIME_MIN_MS = 1200;
const LIFETIME_MAX_MS = 2600;
const SIZE_MIN_PX = 0.8;
const SIZE_MAX_PX = 2;
/**
 * Part de la vitesse du roll que la paillette garde en naissant. Nulle : la
 * chute ne dépend que de la gravité, pas du tempo ni du zoom.
 */
const CARRY = 0;
/** Éclatement initial (px/s), de part et d'autre. Modéré : elles s'ouvrent sans s'égailler. */
const SPREAD_X = 60;
const SPREAD_Y = 50;
/** Freinage de l'air, par seconde. Plus fort, la paillette est plus légère : l'air la retient. */
const DRAG = 2.6;
/** Chute (px/s²). La vitesse limite vaut GRAVITY / DRAG : ~60 px/s, une chute de poussière. */
const GRAVITY = 155;
/**
 * La dérive : chaque paillette pousse dans une direction qui tourne lentement
 * au hasard. C'est ce qui les diffuse — sans elle, la gerbe garde la forme de
 * son éclatement et retombe d'un bloc. Plus faible que la chute, pour
 * qu'elles descendent en ondoyant plutôt que de s'égailler.
 */
const WANDER_ACCEL = 60;
/** Vitesse (rad/s) à laquelle la direction de dérive peut tourner. */
const WANDER_TURN = 3;
/** Rotation maximale (rad/s) — le carré qui tourne, c'est ce qui brille. */
const SPIN_MAX = 4;
const TWINKLE_MIN_HZ = 1.5;
const TWINKLE_MAX_HZ = 4;
/** Éclat maximal, et part de l'éclat que le scintillement fait varier. */
const PEAK_ALPHA = 0.6;
const TWINKLE_DEPTH = 0.45;
const WHITE_MIX_MIN = 0.2;
const WHITE_MIX_MAX = 0.85;
/** Hauteur (px) sur laquelle une paillette pâlit en approchant du clavier. */
const EDGE_FADE_PX = 14;
/** Au-delà de ce saut d'une image à l'autre, c'est un déplacement, pas de la lecture. */
const MAX_JUMP_PX = 400;

/**
 * Sinus, cosinus et extinction en tables.
 *
 * Quatre fonctions transcendantes par paillette et par image — deux pour la
 * dérive, une pour le scintillement, une pour l'extinction — c'était le poste
 * le plus lourd de la boucle sur un millier de paillettes. Une table de 256
 * pas les remplace : 1,4° de quantification sur une direction qui tourne au
 * hasard, et 256 marches sur une extinction qui finit de toute façon en alpha
 * sur 8 bits. Rien de visible, trois fois moins de temps.
 */
const TRIG_STEPS = 256;
const TAU = Math.PI * 2;
const SIN = new Float32Array(TRIG_STEPS);
const COS = new Float32Array(TRIG_STEPS);

for (let i = 0; i < TRIG_STEPS; i++) {
  SIN[i] = Math.sin((i / TRIG_STEPS) * TAU);
  COS[i] = Math.cos((i / TRIG_STEPS) * TAU);
}

/** Radians → indice de table. Le `& (TRIG_STEPS - 1)` replie, négatifs compris. */
const TRIG_SCALE = TRIG_STEPS / TAU;

const FADE_STEPS = 256;
const FADE = new Float32Array(FADE_STEPS);

for (let i = 0; i < FADE_STEPS; i++) {
  FADE[i] = Math.pow(1 - i / (FADE_STEPS - 1), 1.2);
}

type Dust = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ageMS: number;
  lifeMS: number;
  wander: number;
  size: number;
  spin: number;
  phase: number;
  twinkle: number;
  particle: Particle;
};

export type NoteDustProps = {
  entries: readonly NoteEntry[];
  fromTick: number;
  toTick: number;
  height: number;
  playheadY: number;
  laneColors: readonly LaneStyle[];
  deltaMS: number;
};

function towardWhite(color: number, factor: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;

  return (
    (Math.round(r + (255 - r) * factor) << 16)
    | (Math.round(g + (255 - g) * factor) << 8)
    | Math.round(b + (255 - b) * factor)
  );
}

function between(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Les notes partent en paillettes en passant la tête de lecture, et les
 * paillettes se diffusent dans l'air jusqu'à s'y dissoudre.
 *
 * À chaque image de lecture, la ligne a mangé une tranche de chaque note qui
 * la traverse — la hauteur défilée depuis l'image précédente, sur la largeur
 * de la touche. Cette surface devient des paillettes de la couleur de la
 * note : elles naissent dans la tranche, gardent un peu de l'élan du roll et
 * s'éparpillent. Puis chacune dérive dans une direction qui tourne lentement
 * au hasard : la gerbe s'élargit en nuage, en tournant et en scintillant,
 * pendant que les paillettes pâlissent et s'éteignent.
 *
 * Elles partent toutes vers le bas : l'éclatement ne remonte pas, et la ligne
 * est un plafond que la dérive ne franchit jamais — rien ne passe sur les
 * notes à venir. Contre le clavier, elles pâlissent pour ne pas déborder.
 *
 * Rien n'est émis à l'arrêt ni sur un saut (déplacement, boucle). Les traces
 * du jeu (lanes en contour) ne s'émiettent pas : ce sont des relevés, pas des
 * consignes. La note elle-même n'est plus dessinée sous la ligne (`Notes`).
 *
 * Même rendu que les particules du halo : un `ParticleContainer`, en additif,
 * et des paillettes recyclées plutôt que réallouées.
 */
export class NoteDust {
  private layer: ParticleLayer;
  private live: Dust[] = [];
  private free: Dust[] = [];
  private previousFromTick: number | null = null;
  /** Éteintes tant que `setEnabled` ne dit pas le contraire : le roll d'origine. */
  private enabled = false;
  private floorY = 0;
  private lineY = 0;

  constructor(parent: Container, private layout: PianoLayout, private pianoComponent: PianoComponent) {
    // `vertex` porte l'échelle : il n'existe pas de propriété `scale` dans
    // Pixi, et le nom était ignoré en silence — la taille des paillettes ne
    // partait au GPU que par accident, parce que les naissances salissaient le
    // tampon statique à chaque image. Elle est maintenant dynamique pour de
    // bon, et le statique se fige (cf. `ParticleLayer`).
    this.layer = new ParticleLayer(parent, {
      position: true,
      rotation: true,
      vertex: true,
      color: true,
      uvs: false,
    });
  }

  emit(props: NoteDustProps): void {
    if (!this.enabled) {
      return;
    }

    const { entries, fromTick, toTick, height, playheadY, laneColors, deltaMS } = props;
    const previous = this.previousFromTick;
    this.previousFromTick = fromTick;
    this.floorY = height;
    this.lineY = playheadY;

    if (previous === null || deltaMS <= 0 || toTick <= fromTick) {
      return;
    }

    const pxPerTick = height / (toTick - fromTick);
    const consumedPx = (fromTick - previous) * pxPerTick;

    if (consumedPx <= 0 || consumedPx > MAX_JUMP_PX) {
      return;
    }

    const rollSpeed = consumedPx / (deltaMS / 1000);
    const lineTick = fromTick + (height - playheadY) / pxPerTick;
    const previousLineTick = lineTick - (fromTick - previous);
    const projection = this.pianoComponent.getProjection();
    let budget = MAX_EMIT_PER_FRAME;

    for (const entry of entries) {
      if (budget <= 0) {
        break;
      }

      const lane = laneColors[entry.laneIdx] ?? laneColors[entry.laneIdx % Math.max(1, laneColors.length)];

      if (!lane || lane.outline) {
        continue;
      }

      const from = Math.max(entry.tick, previousLineTick);
      const to = Math.min(entry.noteEndTick, lineTick);

      if (to <= from) {
        continue;
      }

      const keyLayout = this.layout.getKeyByMidi(entry.playedNote.note.toMidi());

      if (!keyLayout) {
        continue;
      }

      const x = projection.keyLeft(keyLayout);
      const w = projection.keyWidth(keyLayout);
      const sliceTop = playheadY + (lineTick - to) * pxPerTick;
      const sliceHeight = (to - from) * pxPerTick;

      // Arrondi au hasard : une note fine qui ne mérite que 0,3 paillette par
      // image en lâche bien une toutes les trois images, pas jamais.
      const wanted = (sliceHeight * w) / EMIT_AREA_PX2;
      const count = Math.min(budget, Math.floor(wanted) + (Math.random() < wanted % 1 ? 1 : 0));

      for (let i = 0; i < count; i++) {
        this.spawn(x + Math.random() * w, sliceTop + Math.random() * sliceHeight, lane.fill, rollSpeed);
      }

      budget -= count;
    }

    this.layer.flush();
  }

  update(deltaMS: number): void {
    const dt = deltaMS / 1000;
    const drag = Math.exp(-DRAG * dt);
    const turn = 2 * WANDER_TURN * dt;
    const accel = WANDER_ACCEL * dt;
    const fall = GRAVITY * dt;

    for (let i = this.live.length - 1; i >= 0; i--) {
      const d = this.live[i];
      d.ageMS += deltaMS;

      if (d.ageMS >= d.lifeMS) {
        this.retire(i);
        continue;
      }

      d.wander += (Math.random() - 0.5) * turn;

      const dir = (d.wander * TRIG_SCALE) & (TRIG_STEPS - 1);

      d.vx = d.vx * drag + COS[dir] * accel;
      d.vy = d.vy * drag + SIN[dir] * accel + fall;
      d.x += d.vx * dt;
      d.y += d.vy * dt;

      // La ligne est un plafond : la dérive peut pousser vers le haut, jamais
      // au-delà de la tête de lecture. Une demi-taille en dessous, puisque la
      // paillette est centrée sur sa position — posée sur la ligne, elle en
      // dépassait encore de moitié.
      const ceiling = this.lineY + d.size / 2;

      if (d.y < ceiling) {
        d.y = ceiling;
        d.vy = Math.max(0, d.vy);
      }

      const t = d.ageMS / d.lifeMS;
      const fade = FADE[(t * (FADE_STEPS - 1)) | 0];
      const edges = clamp01((this.floorY - d.y) / EDGE_FADE_PX);
      const phase = ((d.phase + d.ageMS * 0.001 * d.twinkle * TAU) * TRIG_SCALE) & (TRIG_STEPS - 1);
      const glint = 0.5 + 0.5 * SIN[phase];
      const size = d.size * (1 - 0.5 * t);

      d.particle.x = d.x;
      d.particle.y = d.y;
      d.particle.rotation += d.spin * dt;
      d.particle.scaleX = size;
      d.particle.scaleY = size;
      d.particle.alpha = PEAK_ALPHA * fade * edges * (1 - TWINKLE_DEPTH + TWINKLE_DEPTH * glint);
    }

    this.layer.flush();
  }

  isAnimating(): boolean {
    return this.live.length > 0;
  }

  /**
   * Allume ou éteint les paillettes. Éteintes, celles en vol disparaissent
   * aussitôt et plus rien n'est émis.
   */
  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) {
      return;
    }

    this.enabled = enabled;
    this.previousFromTick = null;

    for (let i = this.live.length - 1; i >= 0; i--) {
      this.retire(i);
    }
  }

  destroy(): void {
    this.live.length = 0;
    this.free.length = 0;
    this.layer.destroy();
  }

  private spawn(x: number, y: number, color: number, rollSpeed: number): void {
    let d = this.free.pop();

    if (!d) {
      if (this.live.length >= MAX_PARTICLES) {
        return;
      }

      d = this.create();
    }

    d.x = x;
    d.y = y;
    d.vx = (Math.random() - 0.5) * 2 * SPREAD_X;
    // Vers le bas seulement : une paillette ne remonte pas sur les notes à venir.
    d.vy = rollSpeed * CARRY + Math.random() * SPREAD_Y;
    d.ageMS = 0;
    d.lifeMS = between(LIFETIME_MIN_MS, LIFETIME_MAX_MS);
    d.wander = Math.random() * Math.PI * 2;
    d.size = between(SIZE_MIN_PX, SIZE_MAX_PX);
    d.spin = (Math.random() - 0.5) * 2 * SPIN_MAX;
    d.phase = Math.random() * Math.PI * 2;
    d.twinkle = between(TWINKLE_MIN_HZ, TWINKLE_MAX_HZ);

    d.particle.x = x;
    d.particle.y = y;
    d.particle.rotation = Math.random() * Math.PI;
    d.particle.scaleX = d.size;
    d.particle.scaleY = d.size;
    d.particle.tint = towardWhite(color, between(WHITE_MIX_MIN, WHITE_MIX_MAX));
    d.particle.alpha = 1;

    // Les deux tableaux vivent indice par indice : `live[i]` dessine
    // `particleChildren[i]`, à la naissance comme à la mort.
    this.layer.add(d.particle);
    this.live.push(d);
  }

  private retire(index: number): void {
    const d = this.live[index];
    const last = this.live.length - 1;

    this.layer.removeAt(index);
    this.live[index] = this.live[last];
    this.live.length = last;
    this.free.push(d);
  }

  private create(): Dust {
    return {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      ageMS: 0,
      lifeMS: LIFETIME_MIN_MS,
      wander: 0,
      size: SIZE_MIN_PX,
      spin: 0,
      phase: 0,
      twinkle: TWINKLE_MIN_HZ,
      particle: new Particle({ texture: Texture.WHITE, anchorX: 0.5, anchorY: 0.5, alpha: 0 }),
    };
  }
}
